import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { computePlan, GOAL_LABELS, ACTIVITY_LABELS } from "@/lib/nutrition";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Loader2, Check, Target, Activity, Salad, Flame, Trophy,
} from "lucide-react";

const GOALS = [
  { v: "lose", label: "Lose weight", emoji: "🔻" },
  { v: "maintain", label: "Maintain my weight", emoji: "⚖️" },
  { v: "gain", label: "Gain weight", emoji: "🔺" },
  { v: "muscle", label: "Build muscle", emoji: "💪" },
  { v: "nutrition", label: "Improve my overall nutrition", emoji: "🥗" },
];
const ACTIVITIES = [
  { v: "sedentary", label: "Mostly sedentary", sub: "Little to no exercise" },
  { v: "light", label: "Lightly active", sub: "1–2 days / week" },
  { v: "moderate", label: "Moderately active", sub: "3–4 days / week" },
  { v: "very", label: "Very active", sub: "5–6 days / week" },
  { v: "extreme", label: "Extremely active", sub: "Daily / athlete" },
];
const DIETS = [
  "No preference", "Vegetarian", "Vegan", "High protein", "Low carb", "Mediterranean", "Other",
];
const HABITS = [
  "Eating too much", "Too much sugar", "Too much fast food", "Late-night eating",
  "Not enough protein", "Not enough vegetables", "Irregular meals", "Portion control",
  "Too many sugary drinks",
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { profile, setProfile, loadProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: profile?.name || "",
    age: "", gender: "", height: "", current_weight: "",
    target_weight: "", goal: "", activity_level: "", dietary_preference: "",
    habits: [],
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleHabit = (h) =>
    setForm((f) => ({
      ...f,
      habits: f.habits.includes(h) ? f.habits.filter((x) => x !== h) : [...f.habits, h],
    }));

  const total = 7;
  const go = (d) => {
    setDir(d);
    setStep((s) => Math.min(total - 1, Math.max(0, s + d)));
  };

  const canNext = () => {
    if (step === 0) return form.name && form.age && form.gender && form.height && form.current_weight;
    if (step === 1) return form.goal;
    if (step === 2) return form.target_weight;
    if (step === 3) return form.activity_level;
    if (step === 4) return form.dietary_preference;
    return true;
  };

  const finish = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        age: Number(form.age),
        height: Number(form.height),
        current_weight: Number(form.current_weight),
        target_weight: Number(form.target_weight),
        onboarding_completed: true,
      };
      const updated = await api.updateProfile(payload);
      setProfile(updated);
      await loadProfile();
      toast.success("Your plan is ready!");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e.message || "Could not save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const plan = computePlan(form);

  return (
    <div className="min-h-screen bg-background flex flex-col max-w-md mx-auto">
      <div className="px-6 pt-8 pb-4">
        <div className="flex items-center justify-between mb-3">
          {step > 0 ? (
            <button onClick={() => go(-1)} data-testid="onb-back" className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <span className="w-5" />
          )}
          <span className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground">
            Step {step + 1} of {total}
          </span>
          <span className="w-5" />
        </div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-primary rounded-full"
            style={{ width: `${((step + 1) / total) * 100}%`, transition: "width 0.5s cubic-bezier(0.22,1,0.36,1)" }}
          />
        </div>
      </div>

      <div className="flex-1 px-6 overflow-y-auto no-scrollbar">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={step}
            custom={dir}
            initial={{ opacity: 0, x: dir * 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -40 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="py-4"
          >
            {step === 0 && <BasicStep form={form} set={set} />}
            {step === 1 && (
              <ChoiceStep
                icon={Target}
                title="What are you trying to achieve?"
                options={GOALS.map((g) => ({ v: g.v, label: g.label, emoji: g.emoji }))}
                value={form.goal}
                onSelect={(v) => set("goal", v)}
                testid="goal"
              />
            )}
            {step === 2 && (
              <div className="space-y-6">
                <Header icon={Flame} title="What's your target weight?" sub="We'll pace your plan to get you there safely." />
                <div className="rounded-3xl bg-card border border-border p-6 text-center">
                  <div className="flex items-end justify-center gap-2">
                    <input
                      data-testid="target-weight-input"
                      type="number"
                      value={form.target_weight}
                      onChange={(e) => set("target_weight", e.target.value)}
                      placeholder="70"
                      className="w-32 text-center text-5xl font-display font-medium bg-transparent outline-none tabular-nums"
                    />
                    <span className="text-xl text-muted-foreground mb-2">kg</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Current: {form.current_weight || "—"} kg
                  </p>
                </div>
              </div>
            )}
            {step === 3 && (
              <ChoiceStep
                icon={Activity}
                title="How active are you?"
                options={ACTIVITIES}
                value={form.activity_level}
                onSelect={(v) => set("activity_level", v)}
                testid="activity"
              />
            )}
            {step === 4 && (
              <ChoiceStep
                icon={Salad}
                title="Any dietary preferences?"
                options={DIETS.map((d) => ({ v: d, label: d }))}
                value={form.dietary_preference}
                onSelect={(v) => set("dietary_preference", v)}
                testid="diet"
              />
            )}
            {step === 5 && (
              <div className="space-y-5">
                <Header icon={Check} title="What would you like to improve?" sub="Select all that apply — we'll keep them in mind." />
                <div className="flex flex-wrap gap-2.5">
                  {HABITS.map((h) => {
                    const active = form.habits.includes(h);
                    return (
                      <button
                        key={h}
                        data-testid={`habit-${h.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() => toggleHabit(h)}
                        className={`px-4 py-2.5 rounded-full text-sm font-semibold border transition-colors ${
                          active
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-card text-foreground/80 border-border hover:border-primary/40"
                        }`}
                      >
                        {h}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {step === 6 && <Summary form={form} plan={plan} />}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="px-6 py-6">
        {step < total - 1 ? (
          <Button
            onClick={() => go(1)}
            disabled={!canNext()}
            data-testid="onb-next"
            className="w-full rounded-full py-6 text-base font-semibold bg-primary hover:bg-primary/90"
          >
            Continue <ArrowRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            onClick={finish}
            disabled={busy}
            data-testid="onb-finish"
            className="w-full rounded-full py-6 text-base font-semibold bg-primary hover:bg-primary/90"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
            Start my journey
          </Button>
        )}
      </div>
    </div>
  );
}

function Header({ icon: Icon, title, sub }) {
  return (
    <div className="space-y-3">
      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
        <Icon className="w-6 h-6 text-primary" />
      </div>
      <h2 className="font-display text-3xl font-medium tracking-tight leading-tight">{title}</h2>
      {sub && <p className="text-muted-foreground">{sub}</p>}
    </div>
  );
}

function BasicStep({ form, set }) {
  return (
    <div className="space-y-5">
      <Header icon={Target} title="Let's get to know you" sub="This helps us tailor your calorie and macro targets." />
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-semibold text-foreground/70">Name</Label>
          <Input data-testid="name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="h-12 rounded-xl bg-card" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-foreground/70">Age</Label>
            <Input data-testid="age-input" type="number" value={form.age} onChange={(e) => set("age", e.target.value)} placeholder="28" className="h-12 rounded-xl bg-card" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-foreground/70">Gender</Label>
            <div className="flex gap-2">
              {["male", "female"].map((g) => (
                <button
                  key={g}
                  data-testid={`gender-${g}`}
                  onClick={() => set("gender", g)}
                  className={`flex-1 h-12 rounded-xl text-sm font-semibold border capitalize transition-colors ${
                    form.gender === g ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-foreground/70">Height (cm)</Label>
            <Input data-testid="height-input" type="number" value={form.height} onChange={(e) => set("height", e.target.value)} placeholder="175" className="h-12 rounded-xl bg-card" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-foreground/70">Weight (kg)</Label>
            <Input data-testid="weight-input" type="number" value={form.current_weight} onChange={(e) => set("current_weight", e.target.value)} placeholder="72" className="h-12 rounded-xl bg-card" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ChoiceStep({ icon, title, options, value, onSelect, testid }) {
  return (
    <div className="space-y-5">
      <Header icon={icon} title={title} />
      <div className="space-y-2.5">
        {options.map((o) => {
          const active = value === o.v;
          return (
            <button
              key={o.v}
              data-testid={`${testid}-${o.v}`}
              onClick={() => onSelect(o.v)}
              className={`w-full flex items-center gap-3 p-4 rounded-2xl border text-left transition-all ${
                active ? "bg-primary/5 border-primary shadow-sm" : "bg-card border-border hover:border-primary/40"
              }`}
            >
              {o.emoji && <span className="text-xl">{o.emoji}</span>}
              <div className="flex-1">
                <div className="font-semibold text-foreground">{o.label}</div>
                {o.sub && <div className="text-xs text-muted-foreground">{o.sub}</div>}
              </div>
              <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${active ? "border-primary bg-primary" : "border-border"}`}>
                {active && <Check className="w-3 h-3 text-primary-foreground" />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Summary({ form, plan }) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <Trophy className="w-7 h-7 text-primary" />
        </div>
        <h2 className="font-display text-3xl font-medium tracking-tight">Your plan is ready.</h2>
        <p className="text-muted-foreground">Here's what we've tailored for you.</p>
      </div>

      <div className="rounded-3xl bg-primary text-primary-foreground p-6 text-center">
        <p className="text-xs uppercase tracking-[0.15em] opacity-80">Daily calorie target</p>
        <p className="text-5xl font-display font-medium mt-1 tabular-nums">{plan.calorie_goal.toLocaleString()}</p>
        <p className="text-sm opacity-80">kcal / day</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Stat label="Current weight" value={`${form.current_weight} kg`} />
        <Stat label="Target weight" value={`${form.target_weight} kg`} />
        <Stat label="Protein target" value={`${plan.protein_goal} g`} />
        <Stat label="Activity" value={ACTIVITY_LABELS[form.activity_level]?.split(" ")[0] || "—"} />
      </div>
      <div className="rounded-2xl bg-card border border-border p-4">
        <p className="text-sm text-muted-foreground">
          Goal: <span className="text-foreground font-semibold">{GOAL_LABELS[form.goal]}</span>
        </p>
      </div>
      <p className="text-center font-display text-lg text-foreground/80">Let's make every meal count.</p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-display font-medium mt-0.5">{value}</p>
    </div>
  );
}
